/**
 * First Setup wizard — replica della sezione First Setup mode del local
 * tool HTML. Pensato per i nuovi dipendenti (Chikako, Matteo, Monica,
 * etc.): mostra una sidebar con steps numerati (Preparation -> M365 ->
 * Google Workspace -> OTA -> ... -> Booking Manager) e un main panel
 * con le credenziali da configurare in quel passo.
 *
 * Gli step sono generati dinamicamente dalle categorie del vault +
 * uno step preliminare "Preparation" (install LINE + Chrome) e uno
 * finale "Done".
 *
 * Accesso: stessa autenticazione del vault. Si entra dalla topbar
 * (route "first-setup").
 */

import { html, nothing, type TemplateResult } from "lit-html";

import { api, ApiError } from "../api.js";
import {
  decryptItem,
  type UnlockedState,
} from "../client-crypto.js";
import { rerender, state as appState } from "../main.js";

interface RawItem {
  id: string; category_id: string; scope: "personal" | "company"; version: number;
  name_ct_b64: string; name_iv_b64: string;
  ciphertext_b64: string; iv_b64: string;
}

interface DecryptedListed {
  raw: RawItem;
  name: string;
  value: Record<string, unknown>;
}

const cache = new Map<string, DecryptedListed>();

const s = {
  loaded: false,
  step: 0,
  items: [] as DecryptedListed[],
  categories: [] as Array<{ id: string; slug: string; label_en: string; label_jp: string; icon_name: string; default_brand_color: string; order_index?: number }>,
  err: "",
};

const PREPARATION_STEP = {
  id: "preparation",
  label_en: "Preparation",
  label_jp: "準備",
  detail_en: "Before signing in, install the apps used by the team.",
  detail_jp: "サインインの前に、チームが使うアプリをインストールしてください。",
};

const DONE_STEP = {
  id: "done",
  label_en: "All set",
  label_jp: "完了",
  detail_en: "Your accounts are configured. Welcome aboard.",
  detail_jp: "アカウントの設定が完了しました。ようこそ。",
};

async function ensureLoaded(): Promise<void> {
  if (s.loaded) return;
  const unlocked = (window as unknown as { __unlocked?: UnlockedState }).__unlocked;
  if (!unlocked) {
    s.err = appState.lang === "en"
      ? "Vault is locked. Unlock from the Vault tab first."
      : "Vault がロックされています。先に Vault タブからロック解除してください。";
    rerender();
    return;
  }
  try {
    const [resp, cats] = await Promise.all([api.itemsList(), api.categories()]);
    const items: DecryptedListed[] = [];
    for (const raw of resp.items as unknown as RawItem[]) {
      if (cache.has(raw.id)) { items.push(cache.get(raw.id)!); continue; }
      try {
        const itemRead = await api.itemRead(raw.id);
        const dec = await decryptItem({
          item_id: raw.id,
          scope: raw.scope,
          version: raw.version,
          ciphertext_b64: itemRead.item.ciphertext_b64 as string,
          iv_b64: itemRead.item.iv_b64 as string,
          name_ct_b64: itemRead.item.name_ct_b64 as string,
          name_iv_b64: itemRead.item.name_iv_b64 as string,
          ephemeral_pub_jwk: itemRead.wrap.ephemeral_pub_jwk,
          wrapped_ck_b64: itemRead.wrap.wrapped_ck_b64,
          unlocked,
        });
        const item: DecryptedListed = { raw, name: dec.name, value: dec.value as Record<string, unknown> };
        cache.set(raw.id, item);
        items.push(item);
      } catch (err) {
        items.push({ raw, name: "(decrypt failed)", value: { _error: (err as Error).message } });
      }
    }
    s.items = items;
    s.categories = cats.categories as typeof s.categories;
    s.loaded = true;
  } catch (err) {
    s.err = err instanceof ApiError ? err.message : (err as Error).message;
  }
  rerender();
}

function setStep(n: number): void {
  s.step = n;
  setTimeout(() => {
    const panel = document.querySelector(".fs-main");
    if (panel) panel.scrollTop = 0;
  }, 0);
  rerender();
}

// Keyboard navigation (ArrowLeft / ArrowRight + Page Up / Page Down)
// attached on first render. We install once globally; the handler is a
// no-op when state.route !== "first-setup".
let kbInstalled = false;
function installKbNav(stepsCountFn: () => number): void {
  if (kbInstalled) return;
  kbInstalled = true;
  window.addEventListener("keydown", (ev) => {
    if (appState.route !== "first-setup") return;
    const tag = (ev.target as HTMLElement | null)?.tagName?.toUpperCase();
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
    const last = stepsCountFn() - 1;
    if (ev.key === "ArrowRight" || ev.key === "PageDown") {
      ev.preventDefault();
      if (s.step < last) setStep(s.step + 1);
    } else if (ev.key === "ArrowLeft" || ev.key === "PageUp") {
      ev.preventDefault();
      if (s.step > 0) setStep(s.step - 1);
    }
  });
}

function buildSteps(): Array<{ id: string; label_en: string; label_jp: string; categoryId?: string }> {
  const steps: Array<{ id: string; label_en: string; label_jp: string; categoryId?: string }> = [
    { id: PREPARATION_STEP.id, label_en: PREPARATION_STEP.label_en, label_jp: PREPARATION_STEP.label_jp },
  ];
  const orderedCats = [...s.categories].sort((a, b) => (a.order_index ?? 99) - (b.order_index ?? 99));
  for (const cat of orderedCats) {
    const items = s.items.filter((i) => i.raw.category_id === cat.id);
    if (items.length === 0) continue;
    steps.push({ id: cat.id, label_en: cat.label_en, label_jp: cat.label_jp, categoryId: cat.id });
  }
  steps.push({ id: DONE_STEP.id, label_en: DONE_STEP.label_en, label_jp: DONE_STEP.label_jp });
  return steps;
}

function renderPreparation(): TemplateResult {
  return html`
    <div>
      <p class="fs-step-num">
        <span class="num">1</span>
        <span data-lang="en">Step 1</span><span data-lang="jp">ステップ 1</span>
      </p>
      <h2 class="fs-step-h">
        <span data-lang="en">${PREPARATION_STEP.label_en} - Install required apps</span><span data-lang="jp">${PREPARATION_STEP.label_jp} - 必須アプリのインストール</span>
      </h2>
      <p class="fs-step-detail">
        <span data-lang="en">${PREPARATION_STEP.detail_en} Install <b>LINE</b> for internal team messaging and <b>Google Chrome</b> as the default browser for all company tools.</span><span data-lang="jp">${PREPARATION_STEP.detail_jp} <b>LINE</b> (社内連絡) と <b>Google Chrome</b> (会社ツール標準ブラウザ) をインストールしてください。</span>
      </p>
      <div class="fs-prep-grid">
        <div class="fs-prep-card">
          <div class="fs-prep-name">LINE</div>
          <p>
            <span data-lang="en">Business messaging app used internally with the team and the boss.</span><span data-lang="jp">社内チームと社長との連絡に使用するメッセージアプリ。</span>
          </p>
          <div class="fs-prep-links">
            <a class="fs-prep-link" href="https://apps.microsoft.com/detail/xpfcc4cd725961" target="_blank" rel="noopener">Windows</a>
            <a class="fs-prep-link" href="https://line.me/en/download" target="_blank" rel="noopener">Mac</a>
          </div>
        </div>
        <div class="fs-prep-card">
          <div class="fs-prep-name">Google Chrome</div>
          <p>
            <span data-lang="en">Recommended default browser for all company tools and dashboards.</span><span data-lang="jp">会社のすべてのツール・ダッシュボードで推奨されるデフォルトブラウザ。</span>
          </p>
          <div class="fs-prep-links">
            <a class="fs-prep-link" href="https://www.google.com/chrome/" target="_blank" rel="noopener">Windows</a>
            <a class="fs-prep-link" href="https://www.google.com/chrome/" target="_blank" rel="noopener">Mac</a>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderCategoryStep(stepNum: number, cat: typeof s.categories[number], items: DecryptedListed[]): TemplateResult {
  const label = appState.lang === "en" ? cat.label_en : cat.label_jp;
  return html`
    <div>
      <p class="fs-step-num">
        <span class="num">${stepNum}</span>
        <span data-lang="en">Step ${stepNum}</span><span data-lang="jp">ステップ ${stepNum}</span>
      </p>
      <h2 class="fs-step-h">${label}</h2>
      <p class="fs-step-detail">
        <span data-lang="en">Configure each account below. Click any value to copy.</span><span data-lang="jp">下記の各アカウントを設定してください。値をクリックでコピーします。</span>
      </p>
      <div class="fs-cards-grid">
        ${items.map((item) => fsCard(item))}
      </div>
    </div>
  `;
}

function fsCard(item: DecryptedListed): TemplateResult {
  const v = item.value as {
    subtitle?: string;
    open_link?: string;
    kv?: Array<{ id: string; label: string; value: string; is_secret: boolean }>;
    notes?: string;
  };
  return html`
    <div class="fs-card">
      <div class="fs-card-hd">
        <div class="fs-card-name">
          ${item.name}
          ${v.subtitle ? html`<span class="sub">${v.subtitle}</span>` : nothing}
        </div>
        ${v.open_link
          ? html`<a class="fs-card-open" href=${v.open_link} target="_blank" rel="noopener">
              <span data-lang="en">Open</span><span data-lang="jp">開く</span>
            </a>`
          : nothing}
      </div>
      <div class="fs-card-kv">
        ${(v.kv ?? []).map((row) => html`
          <div class="k">${row.label}</div>
          <div class="v">${row.is_secret
            ? html`<span class="secret">${row.value}</span>`
            : row.value}</div>
        `)}
      </div>
      ${v.notes ? html`<p class="fs-card-notes">${v.notes}</p>` : nothing}
    </div>
  `;
}

function renderDone(): TemplateResult {
  return html`
    <div class="fs-done">
      <div class="fs-done-icon">✓</div>
      <h2 class="fs-step-h">
        <span data-lang="en">All set</span><span data-lang="jp">完了</span>
      </h2>
      <p class="fs-step-detail">
        <span data-lang="en">${DONE_STEP.detail_en}</span><span data-lang="jp">${DONE_STEP.detail_jp}</span>
      </p>
      <p>
        <span data-lang="en">You can return to the Vault tab at any time to look up any account.</span><span data-lang="jp">いつでも Vault タブから各アカウントを参照できます。</span>
      </p>
    </div>
  `;
}

export function renderFirstSetup(): TemplateResult {
  void ensureLoaded();
  if (s.err) return html`<div class="page"><p style="color: var(--status-error);">${s.err}</p></div>`;
  if (!s.loaded) return html`<div class="page"><p>
    <span data-lang="en">Loading…</span><span data-lang="jp">読み込み中…</span>
  </p></div>`;
  const steps = buildSteps();
  installKbNav(() => steps.length);
  const current = steps[s.step] ?? steps[0]!;
  const progress = ((s.step + 1) / steps.length) * 100;
  const greeting = appState.user?.name?.split(" ")[0] ?? appState.user?.email ?? "";
  return html`
    <main class="fs-layout">
      <aside class="fs-sidebar">
        <div class="fs-sidebar-hd">
          <div class="fs-star">★</div>
          <div>
            <div class="fs-sidebar-title">
              <span data-lang="en">FIRST SETUP</span><span data-lang="jp">初期設定</span>
            </div>
            <div class="fs-sidebar-welcome">
              <span data-lang="en">Welcome${greeting ? `, ${greeting}` : ""}</span><span data-lang="jp">${greeting ? `${greeting}さん、` : ""}ようこそ</span>
            </div>
          </div>
        </div>
        <div class="fs-progress"><span style="width: ${progress}%;"></span></div>
        <nav>
          ${steps.map((step, idx) => {
            const isActive = idx === s.step;
            const isDone = idx < s.step;
            const label = appState.lang === "en" ? step.label_en : step.label_jp;
            return html`
              <button class="fs-nav-item ${isActive ? "active" : ""} ${isDone ? "done" : ""}"
                @click=${() => setStep(idx)}>
                <span class="sn-num">${isDone ? "" : html`<span>${idx + 1}</span>`}</span>
                <span>${label}</span>
              </button>
            `;
          })}
        </nav>
      </aside>
      <section class="fs-main">
        ${current.id === PREPARATION_STEP.id
          ? renderPreparation()
          : current.id === DONE_STEP.id
            ? renderDone()
            : (() => {
                const cat = s.categories.find((c) => c.id === current.categoryId);
                if (!cat) return html`<p>Unknown step</p>`;
                const items = s.items.filter((i) => i.raw.category_id === cat.id);
                return renderCategoryStep(s.step + 1, cat, items);
              })()}

        <div class="fs-actions">
          <button class="ghost-btn" ?disabled=${s.step === 0}
            @click=${() => setStep(Math.max(0, s.step - 1))}>
            ${"← "}<span data-lang="en">Previous</span><span data-lang="jp">戻る</span>
          </button>
          <div class="fs-step-count">
            <span data-lang="en">Step ${s.step + 1} of ${steps.length}</span><span data-lang="jp">${steps.length} 件中 ${s.step + 1}</span>
          </div>
          <button class="primary" ?disabled=${s.step === steps.length - 1}
            @click=${() => setStep(Math.min(steps.length - 1, s.step + 1))}>
            <span data-lang="en">Next</span><span data-lang="jp">次へ</span>${" →"}
          </button>
        </div>
      </section>
    </main>
  `;
}
